import Phaser from 'phaser';
import { getCosmetic } from './data/cosmetics';

/**
 * Renders a player's equipped whole-body overlays onto a sprite: a glow aura behind the body and
 * a particle trail while moving. Pure vanity — it reads `equipped` (slot -> cosmetic id) and draws;
 * nothing here affects gameplay. The HEAD slot is NOT drawn here: head accessories are baked into a
 * full-sprite variant (`<skin>_<accessory>`) and swapped in by AthleteView, so they integrate with
 * the art perfectly and need no anchors. Aura/trail remain overlays because they work on any athlete.
 */
export class CosmeticView {
  private aura?: Phaser.GameObjects.Image;
  private trail?: Phaser.GameObjects.Particles.ParticleEmitter;
  private t = 0;

  constructor(
    scene: Phaser.Scene,
    container: Phaser.GameObjects.Container,
    _sprite: Phaser.GameObjects.Sprite,
    displayHeight: number,
    _spriteW: number,
    _skinId: string,
    equipped: Record<string, string>,
  ) {
    const H = displayHeight;
    const bodyCY = -H * 0.46; // body centre above the feet (container origin)

    // Aura: a tinted soft disc behind the body, gently pulsing (additive glow).
    const auraId = equipped.aura;
    const auraCos = auraId ? getCosmetic(auraId) : undefined;
    if (auraCos && scene.textures.exists('spark')) {
      this.aura = scene.add.image(0, bodyCY, 'spark').setTint(auraCos.color).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.34);
      this.aura.setScale((H * 1.05) / this.aura.width);
      container.addAt(this.aura, 0); // behind the sprite
    }

    // Trail: particles emitted from the lower body while running.
    const trailId = equipped.trail;
    const trailCos = trailId ? getCosmetic(trailId) : undefined;
    if (trailCos && scene.textures.exists('spark')) {
      this.trail = scene.add.particles(0, bodyCY + H * 0.18, 'spark', {
        lifespan: 520, speed: { min: 20, max: 90 }, angle: { min: 150, max: 210 },
        scale: { start: 0.5, end: 0 }, alpha: { start: 0.85, end: 0 }, gravityY: 60,
        tint: trailCos.tints ?? [trailCos.color], frequency: -1, // manual bursts
        blendMode: trailCos.id === 'trail_fire' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL,
      });
      container.addAt(this.trail, 0);
    }
  }

  /** Call each frame: pulse the aura, puff the trail when moving. */
  update(dt: number, speedNorm: number): void {
    this.t += dt;
    if (this.aura) this.aura.setScale(((1 + Math.sin(this.t * 3) * 0.04)) * this.aura.scaleX).setAlpha(0.28 + Math.sin(this.t * 3) * 0.08);
    if (this.trail && speedNorm > 0.08 && this.t % 0.06 < dt) this.trail.emitParticle(1);
  }

  destroy(): void {
    this.aura?.destroy();
    this.trail?.destroy();
  }
}
